import DownloadRounded from '@mui/icons-material/DownloadRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import ImportExportRounded from '@mui/icons-material/ImportExportRounded';
import UploadRounded from '@mui/icons-material/UploadRounded';
import {
  Box,
  Button,
  Card,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { EmptyState, PageHeader } from '../../components/states';
import { dateTime } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { SectionCard } from '../shop/ui';
import { useDataModels, useImportJobs, type DataModel, type ImportJob } from './api';
import { ExportDialog } from './ExportDialog';

/** How the hub groups the models; a model not listed here lands under "Other". */
const GROUPS: { title: string; hint: string; models: string[] }[] = [
  {
    title: 'Contacts',
    hint: 'Customers and suppliers, matched by name and phone.',
    models: ['contact'],
  },
  {
    title: 'Products & catalog',
    hint: 'Products with variants and pack sizes; prices by SKU; the lists products use.',
    models: [
      'product',
      'variant',
      'category',
      'brand',
      'manufacturer',
      'unit',
      'attribute',
      'pos_category',
    ],
  },
  {
    title: 'Opening data',
    hint: 'Start-up figures from your old books: posted once, never changed by a later import.',
    models: ['contact_opening_balance', 'opening_journal', 'opening_stock'],
  },
  {
    title: 'Setup',
    hint: 'Chart of accounts, taxes, payment methods and stock locations.',
    models: ['account', 'tax_rate', 'payment_method', 'location'],
  },
  {
    title: 'Sales',
    hint: 'Drafts can be updated by import; posted invoices and returns are only created.',
    models: ['quotation', 'sales_order', 'sales_invoice', 'sales_return', 'credit_note'],
  },
  {
    title: 'Purchasing',
    hint: 'Draft orders can be updated by import; receipts, bills and returns are only created.',
    models: ['purchase_order', 'goods_receipt', 'supplier_bill', 'purchase_return', 'debit_note'],
  },
  {
    title: 'Payments',
    hint: 'Receipts and supplier payments, allocated by document number.',
    models: ['payment'],
  },
];

const STATUS: Record<
  ImportJob['status'],
  { label: string; color: 'default' | 'info' | 'success' | 'error' | 'warning' }
> = {
  UPLOADED: { label: 'Uploaded', color: 'default' },
  TESTING: { label: 'Testing', color: 'info' },
  TESTED: { label: 'Tested', color: 'warning' },
  RUNNING: { label: 'Importing', color: 'info' },
  DONE: { label: 'Imported', color: 'success' },
  FAILED: { label: 'Failed', color: 'error' },
};

/** /data — every list the shop can import or export, and the recent imports. */
export function DataHubPage() {
  const { can } = useAuth();
  const { format } = useShop();
  const navigate = useNavigate();
  const models = useDataModels();
  const jobs = useImportJobs(can('data.import'));
  const [exporting, setExporting] = useState<DataModel | null>(null);

  if (models.isLoading) return <LinearProgress />;
  const all = models.data ?? [];
  const known = new Set(GROUPS.flatMap((g) => g.models));
  const groups = [
    ...GROUPS.map((g) => ({
      ...g,
      list: g.models.map((n) => all.find((m) => m.name === n)).filter((m): m is DataModel => !!m),
    })),
    { title: 'Other', hint: '', models: [], list: all.filter((m) => !known.has(m.name)) },
  ].filter((g) => g.list.length > 0);
  const labelOf = (name: string) => all.find((m) => m.name === name)?.label ?? name;

  return (
    <>
      <PageHeader
        title="Import & export"
        subtitle="Move data in and out with Excel or CSV files. Export a list, change it, and import it back to update the same records."
      />
      {groups.length === 0 ? (
        <EmptyState
          icon={<ImportExportRounded />}
          title="Nothing to import or export"
          body="Your role does not allow importing or exporting any list."
        />
      ) : (
        <Stack spacing={2.5}>
          {groups.map((g) => (
            <Card key={g.title}>
              <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.25, pb: 1 }}>
                <Typography variant="h5">{g.title}</Typography>
                {g.hint && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {g.hint}
                  </Typography>
                )}
              </Box>
              {g.list.map((m) => (
                <Stack
                  key={m.name}
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                    px: { xs: 2, sm: 3 },
                    py: 1.25,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600 }}>{m.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Matched by {m.keyLabel.toLowerCase()} or External ID
                    </Typography>
                  </Box>
                  {m.canExport && (
                    <Button
                      size="small"
                      startIcon={<DownloadRounded />}
                      onClick={() => setExporting(m)}
                      aria-label={`Export ${m.label}`}
                    >
                      Export
                    </Button>
                  )}
                  {m.canImport && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<UploadRounded />}
                      onClick={() => void navigate(`/data/import/${m.name}`)}
                      aria-label={`Import ${m.label}`}
                    >
                      Import
                    </Button>
                  )}
                </Stack>
              ))}
            </Card>
          ))}

          {can('data.import') && (
            <SectionCard title="Recent imports" icon={<HistoryRounded />}>
              {jobs.data?.length ? (
                <TableContainer>
                  <Table size="small" aria-label="Recent imports">
                    <TableHead>
                      <TableRow>
                        <TableCell>File</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          List
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          When
                        </TableCell>
                        <TableCell>Result</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {jobs.data.map((j) => (
                        <TableRow
                          key={j.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => void navigate(`/data/import/${j.model}?job=${j.id}`)}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>{j.fileName}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            {labelOf(j.model)}
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            {dateTime(j.createdAt, format)}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                              <Chip size="small" {...STATUS[j.status]} />
                              {j.counts && j.status === 'DONE' && (
                                <Typography variant="caption" color="text.secondary">
                                  {j.counts.created} new · {j.counts.updated} updated
                                  {j.counts.failed ? ` · ${String(j.counts.failed)} problems` : ''}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary">No imports yet.</Typography>
              )}
            </SectionCard>
          )}
        </Stack>
      )}
      {exporting && (
        <ExportDialog
          key={exporting.name}
          model={exporting}
          open
          onClose={() => setExporting(null)}
        />
      )}
    </>
  );
}
